import type { ReactNode } from "react";
import { AlertTriangle, Clock, PencilLine, Sparkles } from "./icons";
import { Badge as BadgePrimitive } from "./primitives";
import { cn } from "./cn";

/**
 * Domain badges. Every variant renders a value the API returned — `source`, `isStale`,
 * `isCritical`, `isActive` — never a derived one (constitution I).
 *
 * Wraps the shadcn primitive in src/components/ui/badge.tsx; this file is the vocabulary, that
 * one is the styling. The mapping from meaning to colour lives here and nowhere else, so "stale
 * is amber" is one edit rather than one per screen.
 *
 * ## Icon plus colour, not colour alone
 *
 * Six states across a dense table cannot be told apart by hue — not by anyone with a colour
 * vision deficiency, and not by anyone printing a price list in mono (SC-006). Each badge carries
 * a mark and a word; the colour is the third signal, not the only one.
 */
export type BadgeKind =
  | "from_discount" // TierPriceRow.source — generated from a discount rule (FR-016)
  | "manually_set" // TierPriceRow.source — an admin set this rate by hand (D-05)
  | "stale" // TierPriceRow.isStale (FR-020)
  | "critical" // StaleRowView.isCritical — drift past the API's threshold (FR-021)
  | "incomplete" // SKU missing a conversion factor (FR-013)
  | "active" // isActive === true — stated only where absence would be ambiguous
  | "inactive"; // isActive === false — deactivated, not deleted

const LABEL: Record<BadgeKind, string> = {
  from_discount: "From rule",
  manually_set: "Set by hand",
  stale: "Stale",
  critical: "Critical",
  incomplete: "Incomplete",
  active: "Active",
  inactive: "Inactive",
};

const ICON: Record<BadgeKind, ReactNode> = {
  from_discount: <Sparkles aria-hidden />,
  manually_set: <PencilLine aria-hidden />,
  stale: <Clock aria-hidden />,
  critical: <AlertTriangle aria-hidden />,
  incomplete: <AlertTriangle aria-hidden />,
  // A status is a solid dot in its own colour — the mark every status in the app's grids carries
  // (`getStatusBadge`), so Active reads the same beside an order's Approved (2026-09-28).
  active: <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-current" />,
  inactive: <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-current" />,
};

const VARIANT: Record<BadgeKind, "default" | "secondary" | "destructive" | "outline"> = {
  from_discount: "secondary",
  manually_set: "secondary",
  stale: "outline",
  critical: "destructive",
  incomplete: "destructive",
  active: "outline",
  inactive: "outline",
};

/** Tone overrides for the states the primitive has no variant for. */
/** Tone overrides: a light tint, the colour's dark step for the word, and a hairline inset edge. */
const TONE: Partial<Record<BadgeKind, string>> = {
  stale: "bg-orange-lighter/60 text-orange-dark ring-orange/30",
  active: "bg-green-lighter/50 text-green-dark ring-green/25",
  inactive: "bg-gray-100 text-gray-600 ring-gray-300/70",
  manually_set: "bg-primary-lighter/60 text-primary-dark ring-primary/25",
};

export function Badge({ kind, children }: { kind: BadgeKind; children?: ReactNode }) {
  return (
    <BadgePrimitive variant={VARIANT[kind]} data-kind={kind} className={cn("gap-1", TONE[kind])}>
      {ICON[kind]}
      {children ?? LABEL[kind]}
    </BadgePrimitive>
  );
}
